// The gather look registry: one World theme look id in, one recipe out
// (R1120, `ui-map-interface.md` §6.2.1).
//
// One field of a World body names a harvesting look: `gather`, which is what
// a Dredger draws while it cuts a deposit. The player picks one of ten; this
// module turns the id into the draw code and the figure row, and decides
// nothing else.
//
// **The arithmetic is not here.** `gather-recipe-kit.ts` holds the row and
// context types, the eases, the grain, the alpha clamps, the deposit
// geometry, the atlas cell keys and every primitive. This module re-exports
// the kit, so a reader and an importer take one name from one place.
//
// **A look resolves through its own recipe file first.** The owner picked ten
// cards of mockup round 055, and the draw code of a picked card lives in
// `gather-recipes-{seabed,matter,hull,field}.ts`, keyed by the look id. The
// four files hold DISJOINT sets of ids, so four port lanes edit four files. A
// look that its file lists in `PENDING` resolves to the AUTO look of the list
// — the recipe AND the figure row — so every id a player can pick draws
// something from the day its data lands, and the recipes arrive one lane at a
// time. `<file>.test.ts` refuses an id that is neither implemented nor
// pending, so a card cannot be forgotten in silence.
//
// This is a leaf (`leaves.test.ts`).

import { GATHER_STYLES } from '../gen/balance';
import { GATHER_LOOKS } from '../gen/presentation';
import type { GatherRow } from '../gen/presentation';
import type { GatherRecipe } from './gather-recipe-kit';
import { RECIPES as SEABED, PENDING as SEABED_PENDING } from './gather-recipes-seabed';
import { RECIPES as MATTER, PENDING as MATTER_PENDING } from './gather-recipes-matter';
import { RECIPES as HULL, PENDING as HULL_PENDING } from './gather-recipes-hull';
import { RECIPES as FIELD, PENDING as FIELD_PENDING } from './gather-recipes-field';
import { lookRegistry, type Look, type RecipeFile } from './recipe-kit';

export * from './gather-recipe-kit';

/** The World field that names a harvesting look. */
export const GATHER_FIELD = 'gather';

/** Every look id, in cycle order. Position 0 is the AUTO look (R822). */
export const GATHER_IDS: readonly string[] = GATHER_STYLES;

/** The AUTO look: the id every pending look draws. */
export const GATHER_AUTO = GATHER_IDS[0];

/** One resolved look: the id asked for, its figures and its draw code. */
export type GatherLook = Look<GatherRow, GatherRecipe>;

/**
 * The four recipe files, in the order of the mockup round's own groups.
 *
 * The four hold disjoint id sets, and `gather-styles.test.ts` pins that: two
 * files that claimed one id would let a merge decide which recipe draws.
 */
const FILES: readonly RecipeFile<GatherRecipe>[] = [
  { recipes: SEABED, pending: SEABED_PENDING },
  { recipes: MATTER, pending: MATTER_PENDING },
  { recipes: HULL, pending: HULL_PENDING },
  { recipes: FIELD, pending: FIELD_PENDING },
];

const REGISTRY = lookRegistry(GATHER_IDS, FILES, GATHER_LOOKS);

/** The look of one id, or the AUTO look for an id this build does not hold. */
export const gatherLook: (id: string) => GatherLook = REGISTRY.look;

/** Which ids each recipe file has built, and which it left pending. */
export const fileHalves = REGISTRY.fileHalves;
